import PublicNav from "@/components/PublicNav";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { ToolCTA } from "@/components/InlineCTA";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Football, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import { useIsAppNavVisible } from "@/hooks/useIsAppNavVisible";

function renderContent(text) {
  // Split by bold markers and render
  return text.split("\n").map((line, i) => {
    if (!line.trim()) return <br key={i} />;

    // H2-style headers (lines that are all caps or start with a clear section header pattern)
    if (line.match(/^[A-Z][A-Z &/:'—\-(),.]+$/) && line.length > 10 && line.length < 100) {
      return <h2 key={i} className="font-heading text-xl text-white uppercase mt-8 mb-3">{line}</h2>;
    }

    // FAQ questions (lines ending with ?)
    if (line.trim().endsWith("?") && line.length < 150 && !line.startsWith("**")) {
      return <p key={i} className="text-sm text-white font-semibold mt-4 mb-1">{line}</p>;
    }

    // Bold markers
    const parts = line.split(/\*\*/);
    const rendered = parts.map((part, j) =>
      j % 2 === 0 ? part : <strong key={j} className="text-white">{part}</strong>
    );

    return <p key={i} className="text-sm text-[#a3a3a3] leading-relaxed mb-3">{rendered}</p>;
  });
}

export default function GuideArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const hideNav = useIsAppNavVisible();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/guides/${slug}`);
        setArticle(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    const displayTitle = slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <SEO title={`${displayTitle} — Dynasty Trade Maker`} path={`/guide/${slug}`} />
        <PublicNav />
        <div className="max-w-3xl mx-auto px-4 py-10 space-y-4">
          <div className="h-6 w-32 skeleton rounded-sm" />
          <div className="h-10 w-3/4 skeleton rounded-sm" />
          <div className="h-64 skeleton rounded-sm" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <SEO title={`Guide — Dynasty Trade Maker`} path={`/guide/${slug}`} />
        <PublicNav />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="text-[#a3a3a3] text-lg mb-4">Guide not found</p>
          <Link to="/guide" className="text-[#ccff00] text-sm no-underline hover:underline">Browse all guides</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title={article.seo_title || `${article.title} — Dynasty Trade Maker`}
        description={article.meta_description || article.title}
        path={article.url || `/guide/${slug}`}
        image={(() => {
          const heroes = {"dynasty-startup-draft":"https://dynastytrademaker.com/hero-images/guide-startup-draft.jpg","dynasty-trade-deadline":"https://dynastytrademaker.com/hero-images/guide-trade-deadline.jpg","dynasty-rookie-draft":"https://dynastytrademaker.com/hero-images/guide-rookie-draft.jpg","dynasty-rebuild":"https://dynastytrademaker.com/hero-images/guide-rebuild.jpg","superflex-strategy":"https://dynastytrademaker.com/hero-images/guide-superflex.jpg","startup-draft-strategy":"https://dynastytrademaker.com/hero-images/guide-startup-draft.jpg"};
          return heroes[slug];
        })()}
      />
      <PublicNav />

      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/guide" className="inline-flex items-center gap-1 text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline mb-6 uppercase tracking-wider">
          <ArrowLeft size={10} /> GUIDE
        </Link>

        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">GUIDE</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-4" data-testid="guide-article-title">
          {article.title}
        </h1>

        {/* Hero Image */}
        {(() => {
          const heroes = {
            "dynasty-startup-draft": "/hero-images/guide-startup-draft.jpg",
            "dynasty-trade-deadline": "/hero-images/guide-trade-deadline.jpg",
            "dynasty-rookie-draft": "/hero-images/guide-rookie-draft.jpg",
            "dynasty-rebuild": "/hero-images/guide-rebuild.jpg",
            "superflex-strategy": "/hero-images/guide-superflex.jpg",
            "startup-draft-strategy": "/hero-images/guide-startup-draft.jpg",
          };
          const src = heroes[slug];
          return src ? <div className="mb-6 border border-[#262626] rounded-sm overflow-hidden"><img src={src} alt="" className="w-full h-auto" loading="eager" /></div> : null;
        })()}

        <div className="space-y-1">
          {renderContent(article.content)}
        </div>

        {/* Mid-article CTA — contextual based on guide topic */}
        <ToolCTA
          text={slug?.includes("superflex") ? "See how QB values change in Superflex" :
                slug?.includes("rebuild") || slug?.includes("win-now") ? "Is your team a contender or a rebuilder?" :
                slug?.includes("trade") || slug?.includes("fair") ? "Run any trade through the calculator before you offer" :
                slug?.includes("draft") || slug?.includes("startup") ? "Check any player's real-time dynasty value" :
                slug?.includes("glossary") || slug?.includes("sleeper") ? "Try the free dynasty trade tools" :
                "Put this advice to work on your actual roster"}
          subtext="All tools free to start. No credit card required."
          buttonText="TRY IT FREE"
          buttonLink="/trade-calculator"
        />

        {/* CTA */}
        <div className="mt-10 border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
          <h3 className="font-heading text-lg text-white uppercase mb-2">PUT THIS INTO PRACTICE</h3>
          <p className="text-xs text-[#888] mb-4">Use the free Trade Calculator or import your Sleeper league for AI-powered trade suggestions.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/trade-calculator" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold">FREE TRADE CALCULATOR</Link>
            <a href="/" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">Import Your League <ArrowRight size={11} /></a>
          </div>
        </div>

        {/* Newsletter */}
        <NewsletterSignup source={`guide-${slug}`} className="mt-6" />

        <div className="mt-8 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
        </div>
      </div>
    </div>
  );
}

function Nav() {
  return (
    <div className="border-b border-[#262626]">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <div className="w-7 h-7 bg-[#ccff00] flex items-center justify-center rounded-sm"><Football size={14} weight="bold" color="#000" /></div>
          <span className="font-heading text-xl text-white">DYNASTY TRADE<span className="text-[#ccff00]">MAKER</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/rankings" className="text-xs text-[#a3a3a3] hover:text-white no-underline hidden sm:block">Rankings</Link>
          <Link to="/guide" className="text-xs text-[#a3a3a3] hover:text-white no-underline hidden sm:block">Guide</Link>
          <Link to="/trade-calculator" className="text-xs text-[#ccff00] hover:text-white no-underline font-bold hidden sm:block">Calculator</Link>
        </div>
      </div>
    </div>
  );
}
